import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import {
  ACTIVE_STATUSES,
  ASSET_FILE_KIND_LABELS,
  ASSET_KIND_LABELS,
  CONDITION_SCORE_LABELS,
  LIBRARY_UNIT_LABELS,
  ASSET_FILE_KINDS,
  ATTACHMENT_MIMES,
  OBLIGATION_KIND_TEXT,
  SIGNAL_LABELS,
  dueText,
  formatEuro,
  assetTypeInfo,
  nlsfbFullLabel,
  nlsfbNode,
  assetTypeForCode,
  suggestAssetType,
  nlsfbTrail,
  statusLabel,
  todayIso,
  type AssetDetail,
  type AssetDocumentDto,
  type AssetDto,
  type AssetFileKind,
  type AssetKind,
  type AssetObligationDto,
  type BuildingDto,
  type ConditionScore,
  type IssueStatus,
  type LabelLayout,
  type ObligationStatus,
  type PresignResponse,
} from "@klantpunt/shared";
import {
  IconCamera,
  IconChevronRight,
  IconClose,
  IconMore,
  IconPin,
  IconPlus,
  IconTrash,
  IconUpload,
} from "../../app/icons";
import { PanelHead, useSwipeClose } from "../../app/DetailPanel";
import { Modal } from "../../app/Modal";
import { buildingPath } from "../../app/navItems";
import { ToolPopover } from "../../app/ToolPopover";
import { api, ApiError } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { photoUrl, thumbUrl, uploadPhoto } from "../../lib/photos";
import { putToStorage } from "../../lib/upload";
import { PHONE_QUERY, useMediaQuery } from "../../lib/useMediaQuery";
import { useBuildingCache } from "../building/useBuilding";
import { AssetBadge } from "./assetIcons";
import { buildLabelsPdf, downloadPdf } from "./labels";
import { LabelPrintDialog } from "./LabelPrintDialog";
import { isOwnToken } from "./qr";
import { QrCode } from "./QrCode";
import { Scanner } from "./Scanner";
import { fmtDay, ObligationSection, StatusChip } from "../obligations/ObligationSection";
import { RecordDialog } from "../obligations/RecordDialog";
import { useAssetObligations, useObligationsCache } from "../obligations/useObligations";
import { ReplacementSection, useAssetReplacement } from "../mjop/MjopFields";
import { ConditionSection } from "../condition/ConditionSection";
import { MeterSection, nl, openMeterRound, useAssetMeter } from "../meters/MeterSection";

/** Groep in het asset-paneel: tekeningen, handleidingen of overige documenten. */
const docKind = (d: AssetDocumentDto): AssetFileKind =>
  d.type.drawing ? "drawing" : d.type.key === "manual" ? "manual" : "document";

export const assetKey = (id: string) => ["asset", id] as const;
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });

/** Bestandstype raden als de browser het niet weet (bv. .dwg). */
function mimeOf(f: File): string {
  if (f.type) return f.type;
  const ext = f.name.toLowerCase().split(".").pop();
  return ext === "dwg" ? "image/vnd.dwg" : ext === "dxf" ? "image/vnd.dxf" : "application/octet-stream";
}

/** NL-SfB kort: code en het laatste niveau ("31.51 draaideuren"); het hele pad staat in de tooltip. */
function sfbShort(code: string | null): string {
  const n = nlsfbNode(code);
  if (!n) return "Niet ingedeeld";
  const last = nlsfbTrail(n.code).at(-1);
  return last ? `${n.code} ${last}` : n.code;
}

/** Keuring die het eerst aandacht vraagt: verlopen, binnenkort, onbekend, daarna op datum. */
const URGENCY: Record<ObligationStatus, number> = { overdue: 0, soon: 1, unknown: 2, ok: 3, na: 4 };
function byUrgency(a: AssetObligationDto, b: AssetObligationDto) {
  return URGENCY[a.status] - URGENCY[b.status] || (a.nextDueOn ?? "9999").localeCompare(b.nextDueOn ?? "9999");
}

type TabKey = "ov" | "parts" | "ob" | "read" | "iss" | "files";
type Tone = "overdue" | "soon";
interface Attention {
  key: string;
  tone: Tone;
  text: string;
  sub: string;
  tab: TabKey;
}

export interface AssetActions {
  onClose: () => void;
  onEdit: (a: AssetDetail) => void;
  /** Toon de plek op de tekening (verdieping wisselen en centreren); ook voor een onderdeel. */
  onShow: (a: { floorId: string | null; x: number | null; y: number | null; assetKind?: AssetKind }) => void;
  /** Andere asset openen (hoofdasset of onderdeel). */
  onOpenAsset: (id: string) => void;
  /** Nieuw onderdeel onder deze hoofdasset (docs/ASSETS-MJOP.md, Onderdelen). */
  onNewPart: (a: AssetDetail) => void;
  /** Volgende tik op de tekening zet de asset daar neer. */
  onPlace: (a: AssetDetail) => void;
  /** Kopieën één voor één op de tekening plaatsen (na dupliceren). */
  onPlaceCopies: (floorId: string | null, copies: { id: string; code: string }[]) => void;
  onNewIssue: (a: AssetDetail) => void;
  onOpenIssue: (id: string) => void;
}

/**
 * Detail van een asset, arbeidsmiddel of meter. Bovenaan wat het is en waar het staat, dan wat nu
 * aandacht vraagt en één of twee knoppen voor het werk dat bij de soort hoort (op de telefoon onderin).
 * De rest staat in tabbladen: Overzicht, Onderdelen (als die er zijn), Onderhoud of Keuringen (meter: Opnames),
 * Bevindingen en Bestanden.
 * Bewerken, Dupliceren en Plaatsen staan onder ⋯.
 */
export function AssetPanel({
  building: b,
  assetId,
  actions,
}: {
  building: BuildingDto;
  assetId: string;
  actions: AssetActions;
}) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const cache = useBuildingCache(b.id);
  const phone = useMediaQuery(PHONE_QUERY);
  const q = useQuery({ queryKey: assetKey(assetId), queryFn: () => api<AssetDetail>(`/assets/${assetId}`) });
  const obq = useAssetObligations(b.id, assetId);
  const setObligations = useObligationsCache(b.id, assetId);
  const [tab, setTab] = useState<TabKey>("ov");
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const [linking, setLinking] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [labelDialog, setLabelDialog] = useState(false);
  const [recordFor, setRecordFor] = useState<AssetObligationDto | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const swipeRef = useSwipeClose<HTMLDivElement>(actions.onClose);
  const photoInput = useRef<HTMLInputElement>(null);
  const [uploadKind, setUploadKind] = useState<AssetFileKind>("document");
  const canManage = can(me, "asset.manage");
  const canIssue = can(me, "issue.create");
  const isMeter = q.data?.assetKind === "meter";
  const meterQ = useAssetMeter(assetId, isMeter && hasModule(me, "meters"));
  const replQ = useAssetReplacement(assetId);
  const tabsId = useId();

  const set = (d: AssetDetail) => {
    qc.setQueryData(assetKey(d.id), d);
    cache.upsertAsset(d);
  };
  async function run(fn: () => Promise<AssetDetail | void>, ok?: string) {
    setBusy(true);
    try {
      const d = await fn();
      if (d) set(d);
      if (ok) toast(ok);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  if (q.isPending)
    return (
      <div className="detail">
        <Loading />
      </div>
    );
  if (!q.data)
    return (
      <div className="detail" role="region" aria-label="Asset">
        <p>Deze asset bestaat niet (meer).</p>
        <button type="button" className="btn small" onClick={actions.onClose}>
          Sluiten
        </button>
      </div>
    );
  const a = q.data;
  const photos = a.files.filter((f) => f.kind === "photo");
  const docs = a.documents ?? [];
  const floor = b.floors.find((f) => f.id === a.floorId);
  const room = b.rooms.find((r) => r.id === a.roomId);
  const warrantyOver = a.warrantyUntil ? new Date(`${a.warrantyUntil}T23:59:59`) < new Date() : false;
  const today = todayIso();
  const equipment = a.assetKind === "equipment";
  const typeInfo = assetTypeInfo(a.assetType);
  /** Voorstel voor het symbool van een bestaande asset (docs/SYMBOLEN.md); nooit stil opgeslagen. */
  const fullSfb = me?.org?.elementCoding ?? false;
  const sugRaw = assetTypeInfo(
    !isMeter && !a.assetType && !assetTypeForCode(a.elementCode) ? suggestAssetType(a.name, a.elementCode) : null,
  );
  // Alleen NL-SfB: met de volledige tabel stelt het de code van het symbool voor (alleen dieper, nooit ernaast).
  const sugCode = sugRaw ? (fullSfb ? sugRaw.code : (a.elementCode ?? sugRaw.element)) : null;
  const sugType = sugRaw && sugCode && (!a.elementCode || sugCode.startsWith(a.elementCode)) ? sugRaw : undefined;
  const obligations = obq.data?.obligations ?? [];
  const active = obligations.filter((o) => !o.notApplicable && o.status !== "na").sort(byUrgency);
  const openIssues = a.issues.filter((i) => ACTIVE_STATUSES.has(i.status as IssueStatus));
  const meter = meterQ.data;
  const seeMjop = hasModule(me, "mjop");
  const seeCondition = hasModule(me, "condition");

  // Wat nu aandacht vraagt (eerst het ergste); tikken gaat naar het tabblad waar je het oplost.
  const attention: Attention[] = [];
  for (const o of active) {
    if (equipment && o.lastRecord?.result === "afgekeurd")
      attention.push({ key: `af-${o.id}`, tone: "overdue", text: "Afgekeurd, buiten gebruik", sub: o.name, tab: "ob" });
    else if (o.status === "overdue" || o.status === "soon")
      attention.push({
        key: o.id,
        tone: o.status,
        text:
          o.status === "overdue" && o.lastRecord?.result === "afgekeurd"
            ? `${OBLIGATION_KIND_TEXT[o.kind].noun}: ${OBLIGATION_KIND_TEXT[o.kind].failedStatus.toLowerCase()}`
            : `${OBLIGATION_KIND_TEXT[o.kind].noun} ${o.status === "overdue" ? "verlopen" : "binnenkort"}`,
        sub: `${o.name} · ${fmtDay(o.nextDueOn)} (${dueText(o.nextDueOn, today)})`,
        tab: "ob",
      });
  }
  if (openIssues.length)
    attention.push({
      key: "issues",
      tone: openIssues.some((i) => i.priority === "P1" || i.priority === "P2") ? "overdue" : "soon",
      text: openIssues.length === 1 ? "1 open bevinding" : `${openIssues.length} open bevindingen`,
      sub: openIssues[0]!.title,
      tab: "iss",
    });
  for (const s of meter?.signals ?? [])
    attention.push({
      key: `sig-${s.code}-${s.readingId}`,
      tone: "soon",
      text: SIGNAL_LABELS[s.code],
      sub: s.message,
      tab: "read",
    });
  const attentionKnown = !obq.isPending && (!isMeter || !meterQ.isPending);

  // Knoppen: per soort het werk dat het vaakst nodig is als eerste. Vastleggen opent wat het eerst aan de beurt
  // is en noemt die regel; op het tabblad met de kaarten staat die knop al per regel, dus niet in de balk.
  const recordTarget = canManage && tab !== "ob" ? active[0] : undefined;
  const canRead = isMeter && hasModule(me, "meters", "edit") && Boolean(meter?.meter);
  const bevinding = canIssue && { key: "issue", label: "Bevinding", plus: true, run: () => actions.onNewIssue(a) };
  const keuring = recordTarget && {
    key: "record",
    label: OBLIGATION_KIND_TEXT[recordTarget.kind].record,
    sub: recordTarget.name,
    plus: false,
    run: () => setRecordFor(recordTarget),
  };
  const opname = canRead && {
    key: "read",
    label: "Opname invoeren",
    plus: false,
    run: () => void openMeterRound(a.id, navigate, toast),
  };
  const buttons = (
    (equipment ? [keuring, bevinding] : isMeter ? [opname, bevinding] : [bevinding, keuring]) as (
      { key: string; label: string; sub?: string; plus: boolean; run: () => void } | false | undefined
    )[]
  ).filter((x): x is Exclude<typeof x, false | undefined> => Boolean(x));
  const actionRow = buttons.length > 0 && (
    <div className={`ap-actions${phone ? " foot" : ""}`}>
      {buttons.map((x, i) => (
        <button
          key={x.key}
          type="button"
          className={`btn${i === 0 ? " primary" : ""}${x.sub ? " ap-sub" : ""}`}
          aria-label={x.sub ? `${x.label}: ${x.sub}` : undefined}
          onClick={x.run}
        >
          {x.plus && <IconPlus size={18} aria-hidden="true" />}
          {x.sub ? (
            <span className="ap-subtext">
              <span>{x.label}</span>
              <small>{x.sub}</small>
            </span>
          ) : (
            x.label
          )}
        </button>
      ))}
    </div>
  );

  // Tabbladen per soort. Onderhoud en keuringen staan samen; op de telefoon kort *Onderhoud*.
  const obCount = obligations.filter((o) => !o.notApplicable).length;
  const obLabel = equipment ? "Keuringen" : phone ? "Onderhoud" : "Onderhoud en keuringen";
  const tabs: { key: TabKey; label: string; n?: number }[] = [{ key: "ov", label: "Overzicht" }];
  if (a.parts.length) tabs.push({ key: "parts", label: "Onderdelen", n: a.parts.length });
  if (isMeter) {
    tabs.push({ key: "read", label: "Opnames" });
    if (obCount) tabs.push({ key: "ob", label: obLabel, n: obCount });
  } else tabs.push({ key: "ob", label: obLabel, n: obCount || undefined });
  tabs.push({ key: "iss", label: "Bevindingen", n: openIssues.length || undefined });
  tabs.push({ key: "files", label: "Bestanden", n: a.photoCount + a.fileCount || undefined });
  const current = tabs.some((t) => t.key === tab) ? tab : "ov";
  const tabKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.key === current);
    const j =
      e.key === "ArrowRight" ? (i + 1) % tabs.length : e.key === "ArrowLeft" ? (i - 1 + tabs.length) % tabs.length : -1;
    if (j < 0) return;
    e.preventDefault();
    setTab(tabs[j]!.key);
    (e.currentTarget.querySelectorAll<HTMLButtonElement>("[role=tab]")[j] ?? null)?.focus();
  };

  // Overzicht: korte regels die doorverwijzen naar het tabblad met de details.
  const summary: { key: string; k: string; v: ReactNode; sub?: string; status?: ObligationStatus; tab: TabKey }[] = [];
  const next = active.find((o) => o.nextDueOn);
  if (next)
    summary.push({
      key: "next",
      k: `Volgende ${OBLIGATION_KIND_TEXT[next.kind].noun.toLowerCase()}`,
      v: next.name,
      sub: `${fmtDay(next.nextDueOn)} · ${dueText(next.nextDueOn, today)}`,
      status: next.status,
      tab: "ob",
    });
  if (a.parts.length) {
    const floors = new Set(a.parts.map((p) => p.floorId ?? ""));
    summary.push({
      key: "parts",
      k: "Onderdelen",
      v: a.parts.length === 1 ? "1 onderdeel" : `${a.parts.length} onderdelen`,
      sub: floors.size > 1 ? `op ${floors.size} verdiepingen` : undefined,
      tab: "parts",
    });
  }
  if (seeCondition && a.conditionScore != null)
    summary.push({
      key: "cond",
      k: "Conditie",
      v: `${a.conditionScore} · ${CONDITION_SCORE_LABELS[a.conditionScore as ConditionScore].toLowerCase()}`,
      sub: a.conditionAssessedOn ? `Gemeten ${fmtDay(a.conditionAssessedOn)}` : undefined,
      tab: "ob",
    });
  const repl = seeMjop ? replQ.data : undefined;
  if (repl?.item)
    summary.push({
      key: "repl",
      k: "Vervanging",
      v: `Gepland in ${repl.item.firstYear}`,
      sub: [
        repl.item.cycleYears && `daarna elke ${repl.item.cycleYears} jaar`,
        canManage && repl.unitPriceCents != null && formatEuro(Math.round(repl.unitPriceCents * repl.quantity)),
        canManage &&
          repl.item.indexedCents != null &&
          repl.unitPriceCents != null &&
          repl.item.indexedCents !== Math.round(repl.unitPriceCents * repl.quantity) &&
          `${formatEuro(repl.item.indexedCents)} met index`,
        repl.item.firstYear > repl.horizonEndYear && `na de MJOP-horizon (t/m ${repl.horizonEndYear})`,
      ]
        .filter(Boolean)
        .join(" · "),
      tab: "ob",
    });
  const last = meter?.readings[0];
  if (meter?.meter && last)
    summary.push({
      key: "read",
      k: "Laatste opname",
      v: last.unreachable
        ? "Niet bereikbaar"
        : last.values
            .map((v) => {
              const reg = meter.meter!.registers.find((r) => r.id === v.registerId);
              const label = meter.meter!.registers.length > 1 && reg ? `${reg.name} ` : "";
              return `${label}${nl(v.value)}${meter.meter!.unit ? ` ${meter.meter!.unit}` : ""}`;
            })
            .join(" · "),
      sub: fmtDay(last.readOn),
      tab: "read",
    });

  const fields: [string, string | null][] = [
    [
      "Hoeveelheid",
      a.quantity != null
        ? `${a.quantity.toLocaleString("nl-NL")}${a.unit ? ` ${LIBRARY_UNIT_LABELS[a.unit]}` : ""}`
        : null,
    ],
    ["Merk", a.brand],
    ["Type", a.model],
    ["Serienummer", a.serialNumber],
    ...(a.externalId
      ? [[a.externalSource === "assetlog" ? "Assetlog-code" : "Externe ID", a.externalId] as [string, string]]
      : []),
    ["Capaciteit", a.capacity],
    ["Bouwjaar", a.yearBuilt ? String(a.yearBuilt) : null],
    ["Eind garantie", a.warrantyUntil ? `${fmtDate(a.warrantyUntil)}${warrantyOver ? " (verlopen)" : ""}` : null],
  ];

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    await run(
      async () => {
        let d: AssetDetail | undefined;
        for (const f of Array.from(files)) {
          const mime = mimeOf(f);
          if (!(ATTACHMENT_MIMES as readonly string[]).includes(mime))
            throw new ApiError(400, "type", `${f.name}: dit bestandstype wordt niet ondersteund`);
          const id = crypto.randomUUID();
          const p = await api<PresignResponse>("/files/presign", {
            method: "POST",
            body: { id, kind: "attachment", mime, size: f.size, name: f.name },
          });
          await putToStorage(p, f);
          await api(`/files/${id}/confirm`, { method: "POST", body: {} });
          d = await api<AssetDetail>(`/assets/${a.id}/files`, {
            method: "POST",
            body: { fileId: id, kind: uploadKind },
          });
        }
        return d;
      },
      `${files.length === 1 ? "Document" : `${files.length} documenten`} toegevoegd`,
    );
  }

  /** Foto's om de asset te herkennen (verkleind, met miniatuur); de eerste is de hoofdfoto. */
  async function addPhotos(list: FileList | null) {
    if (!list?.length) return;
    await run(
      async () => {
        let d: AssetDetail | undefined;
        for (const f of Array.from(list)) {
          const fileId = await uploadPhoto(f);
          d = await api<AssetDetail>(`/assets/${a.id}/files`, { method: "POST", body: { fileId, kind: "photo" } });
        }
        return d;
      },
      list.length === 1 ? "Foto toegevoegd" : `${list.length} foto's toegevoegd`,
    );
  }

  async function labelPdf(layout: LabelLayout, skip: number) {
    try {
      const { bytes } = await buildLabelsPdf([a], b.name, layout, { skip });
      downloadPdf(bytes, `QR-label ${a.code}.pdf`);
    } catch (e) {
      toast("Label maken mislukt");
      throw e;
    }
  }

  const menuPick = (fn: () => void) => () => {
    setMenu(false);
    fn();
  };

  return (
    <div ref={swipeRef} className="detail assetdetail" role="region" aria-label={`Asset ${a.code}`}>
      <PanelHead
        code={a.code}
        chip={<span className="chip small">{ASSET_KIND_LABELS[a.assetKind]}</span>}
        actions={
          <>
            {canManage && (
              <ToolPopover
                label="Asset-acties"
                title={`Acties voor ${a.code}`}
                icon={<IconMore size={20} aria-hidden="true" />}
                hideLabel
                open={menu}
                onOpenChange={setMenu}
              >
                <div className="pagemenu">
                  <button type="button" className="menuitem" onClick={menuPick(() => actions.onEdit(a))}>
                    Bewerken
                  </button>
                  <button type="button" className="menuitem" onClick={menuPick(() => setDuplicating(true))}>
                    Dupliceren
                  </button>
                  {!a.parent && (
                    <button type="button" className="menuitem" onClick={menuPick(() => actions.onNewPart(a))}>
                      Onderdeel toevoegen
                    </button>
                  )}
                  {floor?.plan && (
                    <button type="button" className="menuitem" onClick={menuPick(() => actions.onPlace(a))}>
                      {a.x != null ? "Verplaatsen op tekening" : "Op tekening plaatsen"}
                    </button>
                  )}
                </div>
              </ToolPopover>
            )}
          </>
        }
        onClose={actions.onClose}
      />
      <div className="dbody">
        <section className="ap-id" aria-label="Wat en waar">
          {a.photoId ? (
            <a className="ap-photo" href={photoUrl(a.photoId)} target="_blank" rel="noopener">
              <img src={thumbUrl(a.photoId)} alt={`Foto van ${a.code} ${a.name}`} />
            </a>
          ) : canManage ? (
            <button
              type="button"
              className="ap-photo add"
              disabled={busy}
              onClick={() => photoInput.current?.click()}
              aria-label="Foto toevoegen"
            >
              <IconCamera size={24} aria-hidden="true" />
              <span>Foto</span>
            </button>
          ) : (
            <span className="ap-photo">
              <AssetBadge code={a.elementCode} size={40} meter={isMeter} type={a.assetType} />
            </span>
          )}
          <div className="ap-what">
            <h2 className="dtitle">{a.name}</h2>
            {a.parent && (
              <p className="small ap-parent">
                Onderdeel van{" "}
                <button type="button" onClick={() => actions.onOpenAsset(a.parent!.id)}>
                  {a.parent.code} {a.parent.name}
                </button>
              </p>
            )}
            <p className="ap-where">
              {floor?.name ?? "Geen vaste plek"}
              {room && ` › ${room.name}`}
            </p>
            {!isMeter && (
              <p className="muted small" title={nlsfbFullLabel(a.elementCode)}>
                {typeInfo && <b>{typeInfo.label} · </b>}
                NL-SfB {sfbShort(a.elementCode)}
              </p>
            )}
            {sugType && canManage && (
              <div className="catsuggest" role="group" aria-label="Voorstel symbool">
                <span className="muted small">Is dit een</span>
                <button
                  type="button"
                  className="chip chipbtn"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () =>
                        api<AssetDetail>(`/assets/${a.id}`, {
                          method: "PATCH",
                          body: fullSfb ? { elementCode: sugCode, assetType: sugType.key } : { assetType: sugType.key },
                        }),
                      fullSfb ? `${sugType.label}: NL-SfB ${sugCode}` : `${sugType.label}: symbool aangepast`,
                    )
                  }
                >
                  <AssetBadge code={sugCode} type={sugType.key} size={18} />
                  {sugType.label}
                  {fullSfb ? ` (${sugCode})` : ""}?
                </button>
              </div>
            )}
            {a.x != null && a.floorId && (
              <button type="button" className="btn small" onClick={() => actions.onShow(a)}>
                <IconPin size={16} aria-hidden="true" />
                Toon op tekening
              </button>
            )}
          </div>
        </section>

        {attention.length > 0 ? (
          <section className="ap-att" aria-label="Vraagt aandacht">
            <p className="label">Vraagt aandacht</p>
            <ul>
              {attention.map((t) => (
                <li key={t.key}>
                  <button type="button" onClick={() => setTab(t.tab)}>
                    <i className={`ap-dot ob-${t.tone}`} aria-hidden="true" />
                    <span className="grow">
                      <strong>{t.text}</strong>
                      <span className="block muted small">{t.sub}</span>
                    </span>
                    <IconChevronRight size={18} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          attentionKnown && <p className="ap-calm small">Niets dat nu aandacht vraagt.</p>
        )}

        {!phone && actionRow}

        <div className="tabs ap-tabs" role="tablist" aria-label="Tabbladen" onKeyDown={tabKeys}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`${tabsId}-${t.key}`}
              aria-selected={t.key === current}
              aria-controls={`${tabsId}-panel`}
              tabIndex={t.key === current ? 0 : -1}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.n != null && <span className="count">{t.n}</span>}
            </button>
          ))}
        </div>

        <div className="ap-panel" role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-${current}`}>
          {current === "ov" && (
            <>
              {summary.length > 0 && (
                <ul className="ap-sum" aria-label="Kort">
                  {summary.map((s) => (
                    <li key={s.key}>
                      <button type="button" onClick={() => setTab(s.tab)}>
                        <span className="grow">
                          <span className="block muted small">{s.k}</span>
                          <strong className="block">{s.v}</strong>
                          {s.sub && <span className="block muted small">{s.sub}</span>}
                        </span>
                        {s.status && <StatusChip status={s.status} small />}
                        <IconChevronRight size={18} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <Facts fields={fields} notes={a.notes} />
              <section className="sec" aria-label="QR-code">
                <p className="label">QR-code</p>
                {a.qrCode ? (
                  <div className="qrbox">
                    {isOwnToken(a.qrCode) ? (
                      <QrCode code={a.qrCode} size={132} label={`QR-code van ${a.code}`} />
                    ) : (
                      <p className="small">
                        Gekoppelde sticker
                        <br />
                        <code className="qrraw">{a.qrCode}</code>
                      </p>
                    )}
                    <div className="btnrow">
                      <button type="button" className="btn small" onClick={() => setLabelDialog(true)}>
                        Label (PDF)
                      </button>
                      {canManage && (
                        <button
                          type="button"
                          className="btn small"
                          disabled={busy}
                          onClick={() =>
                            run(
                              () => api<AssetDetail>(`/assets/${a.id}/qr`, { method: "DELETE" }),
                              "QR-code ontkoppeld",
                            )
                          }
                        >
                          Ontkoppelen
                        </button>
                      )}
                    </div>
                  </div>
                ) : canManage ? (
                  <div className="btnrow">
                    <button
                      type="button"
                      className="btn small"
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          await api(`/buildings/${b.id}/assets/qr`, { method: "POST", body: { ids: [a.id] } });
                          return api<AssetDetail>(`/assets/${a.id}`);
                        }, "QR-code gemaakt")
                      }
                    >
                      QR-label maken
                    </button>
                    <button type="button" className="btn small" onClick={() => setLinking(true)}>
                      Bestaande sticker koppelen
                    </button>
                  </div>
                ) : (
                  <p className="muted small">Nog geen QR-code.</p>
                )}
              </section>
            </>
          )}

          {current === "read" && (
            <MeterSection key={`m-${a.id}`} asset={a} buildingId={b.id} buildingAssets={b.assets} />
          )}

          {current === "ob" && (
            <>
              <ObligationSection key={`${a.id}-${a.elementCode ?? ""}`} asset={a} buildingId={b.id} />
              <ReplacementSection key={`r-${a.id}-${a.elementCode ?? ""}`} assetId={a.id} />
              <ConditionSection key={`c-${a.id}-${a.conditionScore ?? ""}`} assetId={a.id} />
            </>
          )}

          {current === "parts" && <PartsTab asset={a} building={b} actions={actions} canManage={canManage} />}

          {current === "iss" && <IssuesTab asset={a} actions={actions} />}

          {current === "files" && (
            <>
              <section className="sec" aria-label="Foto's">
                <p className="label">
                  Foto&apos;s <span className="num">{photos.length}</span>
                </p>
                <div className="photos">
                  {photos.map((ph, k) => (
                    <div className="ph phedit" key={ph.fileId}>
                      <a href={photoUrl(ph.fileId)} target="_blank" rel="noopener">
                        <img src={thumbUrl(ph.fileId)} alt={`Foto ${k + 1} van ${a.code}`} loading="lazy" />
                      </a>
                      {canManage && (
                        <button
                          type="button"
                          className="phdel"
                          aria-label={`Foto ${k + 1} verwijderen`}
                          disabled={busy}
                          onClick={() =>
                            run(
                              () => api<AssetDetail>(`/assets/${a.id}/files/${ph.fileId}`, { method: "DELETE" }),
                              "Foto verwijderd",
                            )
                          }
                        >
                          <IconClose size={14} aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  ))}
                  {canManage && (
                    <button type="button" className="add" disabled={busy} onClick={() => photoInput.current?.click()}>
                      +<br />
                      Foto
                    </button>
                  )}
                  {!canManage && photos.length === 0 && <span className="hint">Nog geen foto&apos;s.</span>}
                </div>
              </section>

              <section className="sec" aria-label="Documenten">
                <p className="label attlabel">
                  Documenten <span className="num">{docs.length}</span>
                  {hasModule(me, "dossier") && (
                    <Link className="small attdossier" to={`${buildingPath(b.id, "dossier")}`}>
                      Dossier
                    </Link>
                  )}
                </p>
                {ASSET_FILE_KINDS.map((k) => {
                  const list = docs.filter((d) => docKind(d) === k);
                  return (
                    <div key={k} className="attgroup">
                      <div className="row">
                        <strong className="small">{ASSET_FILE_KIND_LABELS[k]}</strong>
                        <span className="muted small num">{list.length}</span>
                        <span className="grow" />
                        {canManage && (
                          <button
                            type="button"
                            className="btn small ghost"
                            disabled={busy}
                            aria-label={`${ASSET_FILE_KIND_LABELS[k]} toevoegen`}
                            onClick={() => {
                              setUploadKind(k);
                              fileInput.current?.click();
                            }}
                          >
                            <IconUpload size={16} aria-hidden="true" />
                            Toevoegen
                          </button>
                        )}
                      </div>
                      {list.length > 0 && (
                        <ul className="attlist">
                          {list.map((d) => (
                            <li key={d.id}>
                              <a href={`/api/files/${d.current.fileId}/url`} target="_blank" rel="noopener">
                                {d.drawingNumber ? `${d.drawingNumber} ` : ""}
                                {d.title}
                                {d.revision ? ` (rev. ${d.revision})` : ""}
                              </a>
                              {d.via ? (
                                <span className="muted small" title={`Gekoppeld aan de hoofdasset ${d.via.code}`}>
                                  via {d.via.code}
                                </span>
                              ) : d.current.validUntil ? (
                                <span className={`muted small${d.status !== "ok" ? " attwarn" : ""}`}>
                                  {d.status === "expired" ? "verlopen" : "geldig tot"} {fmtDate(d.current.validUntil)}
                                </span>
                              ) : (
                                <span className="muted small">{fmtDate(d.current.createdAt)}</span>
                              )}
                              {canManage && !d.via && (
                                <button
                                  type="button"
                                  className="btn small ghost ico"
                                  aria-label={`${d.title} ontkoppelen van deze asset`}
                                  title="Ontkoppelen (blijft in het dossier)"
                                  disabled={busy}
                                  onClick={() =>
                                    run(
                                      () =>
                                        api<AssetDetail>(`/assets/${a.id}/files/${d.current.fileId}`, {
                                          method: "DELETE",
                                        }),
                                      "Ontkoppeld; het document staat nog in het dossier",
                                    )
                                  }
                                >
                                  <IconTrash size={16} aria-hidden="true" />
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </section>
            </>
          )}
        </div>

        <input
          ref={photoInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          data-testid="asset-photo-input"
          onChange={(e) => {
            const fl = e.target.files;
            void addPhotos(fl).finally(() => (e.target.value = ""));
          }}
        />
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          data-testid="asset-file-input"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt,.dwg,.dxf"
          onChange={(e) => {
            const fl = e.target.files;
            void upload(fl).finally(() => (e.target.value = ""));
          }}
        />
      </div>

      {phone && actionRow}

      {recordFor && (
        <RecordDialog
          asset={a}
          buildingId={b.id}
          obligation={recordFor}
          onClose={() => setRecordFor(null)}
          onSaved={(r) => {
            if (r) setObligations(r);
            setRecordFor(null);
          }}
        />
      )}
      {duplicating && (
        <DuplicateDialog
          asset={a}
          canPlace={Boolean(floor?.plan)}
          onClose={() => setDuplicating(false)}
          onDone={(copies, place) => {
            setDuplicating(false);
            copies.forEach((c) => cache.upsertAsset(c));
            // Codes zijn willekeurig, dus geen reeks "A002 t/m A004" maar een opsomming.
            const shown = copies.slice(0, 3).map((c) => c.code);
            const range = copies.length > 3 ? `${shown.join(", ")} en ${copies.length - 3} meer` : shown.join(", ");
            toast(`${copies.length === 1 ? "Kopie" : `${copies.length} kopieën`} gemaakt: ${range}`);
            if (place) actions.onPlaceCopies(a.floorId, copies);
          }}
        />
      )}
      {linking && (
        <Modal title="Bestaande sticker koppelen" onClose={() => setLinking(false)}>
          <Scanner
            hint={`Scan de QR-sticker die op ${a.code} zit.`}
            onResult={(text) => {
              setLinking(false);
              void run(
                () => api<AssetDetail>(`/assets/${a.id}/qr`, { method: "PUT", body: { qr: text } }),
                "Sticker gekoppeld",
              );
            }}
          />
        </Modal>
      )}
      {labelDialog && <LabelPrintDialog count={1} onClose={() => setLabelDialog(false)} onPrint={labelPdf} />}
    </div>
  );
}

/** Gegevens: alleen ingevulde velden; lege velden op verzoek (aanvullen gaat via Bewerken). */
function Facts({ fields, notes }: { fields: [string, string | null][]; notes: string | null }) {
  const [showEmpty, setShowEmpty] = useState(false);
  const empty = fields.filter(([, v]) => !v).length;
  const shown = showEmpty ? fields : fields.filter(([, v]) => v);
  return (
    <section className="sec" aria-label="Gegevens">
      <p className="label">Gegevens</p>
      {shown.length > 0 ? (
        <dl className="kv">
          {shown.map(([k, v]) => (
            <div key={k} className="kvrow">
              <dt>{k}</dt>
              <dd className={v ? "" : "muted"}>{v ?? "Niet ingevuld"}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="muted small">Nog geen gegevens ingevuld.</p>
      )}
      {empty > 0 && (
        <button type="button" className="ap-more" onClick={() => setShowEmpty(!showEmpty)}>
          {showEmpty ? "Lege velden verbergen" : `${empty} lege ${empty === 1 ? "veld" : "velden"} tonen`}
        </button>
      )}
      {notes && <p className="prewrap small">{notes}</p>}
    </section>
  );
}

/** Bevindingen bij de asset: open bovenaan, afgehandelde ingeklapt. */
/** Onderdelen van een hoofdasset: wat en waar, open bevindingen, en tonen op de tekening. */
function PartsTab({
  asset: a,
  building: b,
  actions,
  canManage,
}: {
  asset: AssetDetail;
  building: BuildingDto;
  actions: AssetActions;
  canManage: boolean;
}) {
  return (
    <section className="sec" aria-label="Onderdelen van deze asset">
      <ul className="list compact partlist">
        {a.parts.map((p) => {
          const floor = b.floors.find((f) => f.id === p.floorId);
          const room = b.rooms.find((r) => r.id === p.roomId);
          return (
            <li key={p.id}>
              <button type="button" className="linkrow" onClick={() => actions.onOpenAsset(p.id)}>
                <span className="code">{p.code}</span>
                <span className="grow">
                  {p.name}
                  <span className="block muted small">
                    {[
                      [p.brand, p.model].filter(Boolean).join(" "),
                      floor ? `${floor.name}${room ? ` › ${room.name}` : ""}` : "Geen vaste plek",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                {p.openIssues > 0 && (
                  <span className="chip" title="Open bevindingen">
                    <i className="ap-dot ob-soon" aria-hidden="true" /> {p.openIssues}
                  </span>
                )}
              </button>
              {p.x != null && p.floorId && (
                <button
                  type="button"
                  className="btn small ghost ico"
                  aria-label={`${p.code} op tekening tonen`}
                  title="Toon op tekening"
                  onClick={() => actions.onShow(p)}
                >
                  <IconPin size={16} aria-hidden="true" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {canManage && (
        <button type="button" className="btn small" onClick={() => actions.onNewPart(a)}>
          <IconPlus size={16} aria-hidden="true" />
          Onderdeel toevoegen
        </button>
      )}
    </section>
  );
}

function IssuesTab({ asset: a, actions }: { asset: AssetDetail; actions: AssetActions }) {
  const open = a.issues.filter((i) => ACTIVE_STATUSES.has(i.status as IssueStatus));
  const done = a.issues.filter((i) => !ACTIVE_STATUSES.has(i.status as IssueStatus));
  const row = (i: AssetDetail["issues"][number]) => (
    <li key={i.id}>
      <button type="button" className="linkrow" onClick={() => actions.onOpenIssue(i.id)}>
        <span className="code">{i.code}</span>
        <span className="grow">
          {i.title}
          {i.asset && <span className="block muted small">{`${i.asset.code} ${i.asset.name}`}</span>}
        </span>
        <span className={`chip st-${i.status}`}>{statusLabel(i.status)}</span>
      </button>
    </li>
  );
  return (
    <section className="sec" aria-label="Bevindingen bij deze asset">
      {open.length ? (
        <ul className="list compact">{open.map(row)}</ul>
      ) : (
        <p className="muted small">Geen open bevindingen.</p>
      )}
      {done.length > 0 && (
        <details className="ap-done">
          <summary className="small">Afgehandeld ({done.length})</summary>
          <ul className="list compact">{done.map(row)}</ul>
        </details>
      )}
    </section>
  );
}

/**
 * Dupliceren (veel gelijke assets, zoals roldeuren in een warehouse): aantal kopieën met dezelfde
 * gegevens, documenten, keuringen en vervanging. Serienummer, QR-code, foto's en plek horen bij het
 * exemplaar en gaan niet mee; plaatsen kan direct daarna, één voor één.
 */
function DuplicateDialog({
  asset: a,
  canPlace,
  onClose,
  onDone,
}: {
  asset: AssetDetail;
  canPlace: boolean;
  onClose: () => void;
  onDone: (copies: AssetDto[], place: boolean) => void;
}) {
  const toast = useToast();
  const [count, setCount] = useState("1");
  const [place, setPlace] = useState(canPlace);
  const [busy, setBusy] = useState(false);
  // Vaste ids per poging: opnieuw proberen na een netwerkfout maakt geen extra kopieën.
  const [ids, setIds] = useState<string[]>([]);
  const n = /^\d+$/.test(count.trim()) ? Number(count.trim()) : 0;
  const ok = n >= 1 && n <= 50;
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ok) return;
    const use = ids.length === n ? ids : Array.from({ length: n }, () => crypto.randomUUID());
    setIds(use);
    setBusy(true);
    try {
      onDone(
        await api<AssetDto[]>(`/assets/${a.id}/duplicate`, { method: "POST", body: { ids: use } }),
        place && canPlace,
      );
    } catch (err) {
      toast(errMsg(err));
      setBusy(false);
    }
  }
  return (
    <Modal
      title={`${a.code} dupliceren`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Annuleren
          </button>
          <button type="submit" form="dupform" className="btn primary" disabled={busy || !ok}>
            {busy ? "Bezig…" : "Dupliceren"}
          </button>
        </>
      }
    >
      <form id="dupform" className="stack" onSubmit={submit}>
        <p className="small">
          Elke kopie krijgt een eigen code en dezelfde gegevens als <strong>{a.name}</strong>: element, merk en type,
          bouwjaar, documenten, keuringen en vervanging. Serienummer, QR-code, foto&apos;s, keuringsdatums en de plek op
          de tekening vul je per exemplaar in.
        </p>
        <label className="field">
          <span className="label">Aantal kopieën</span>
          <input
            inputMode="numeric"
            value={count}
            aria-invalid={!ok}
            onChange={(e) => setCount(e.target.value)}
            autoFocus
          />
          <span className="hint">1 tot 50</span>
        </label>
        {canPlace && (
          <label className="field inline">
            <input type="checkbox" checked={place} onChange={(e) => setPlace(e.target.checked)} />
            <span>Daarna één voor één op de tekening plaatsen</span>
          </label>
        )}
      </form>
    </Modal>
  );
}
